import { toPng } from "html-to-image";
import { ensurePosterFontsLoaded, posterQrTheme } from "./constants";
import { recolorQrCode } from "./recolorQrCode";

function waitForImages(root) {
    const images = [...(root?.querySelectorAll?.("img") || [])];
    return Promise.all(
        images.map((image) => {
            if (image.complete) return Promise.resolve();
            return new Promise((resolve) => {
                image.onload = resolve;
                image.onerror = resolve;
            });
        })
    );
}

async function preparePosterNode(node) {
    ensurePosterFontsLoaded();
    await document.fonts?.ready;
    await waitForImages(node);
}

function posterBadgeText(poster, itemName) {
    if (!poster?.show_badge || !(poster.badge_label || itemName)) return "";
    return [poster.badge_label, itemName].filter(Boolean).join(": ");
}

export function safePosterFilename(name) {
    return (
        String(name || "qr")
            .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, 80) || "qr"
    );
}

async function buildPosterClone(baseNode, { poster, itemName, qrSrc }) {
    const clone = baseNode.cloneNode(true);
    clone.classList.add("print-poster--printing");
    clone.style.transform = "none";
    clone.style.boxShadow = "none";

    const qrTheme = posterQrTheme(poster);
    const recolored = await recolorQrCode(qrSrc, qrTheme).catch(() => qrSrc);
    const frame = clone.querySelector(".print-poster__qr-frame");
    if (frame) {
        let image = frame.querySelector("img");
        if (!image) {
            frame.replaceChildren();
            image = document.createElement("img");
            image.alt = "";
            frame.appendChild(image);
        }
        image.src = recolored;
    }

    const badge = clone.querySelector(".print-poster__badge");
    const badgeText = posterBadgeText(poster, itemName);
    if (badge) {
        if (badgeText) badge.textContent = badgeText;
        else badge.remove();
    }

    return clone;
}

function mountCaptureHost(node) {
    const host = document.createElement("div");
    host.className = "print-poster-capture-host";
    host.appendChild(node);
    document.body.appendChild(host);
    return host;
}

async function posterNodeToPngDataUrl(node) {
    await preparePosterNode(node);
    const width = node.offsetWidth;
    const height = node.offsetHeight;
    return toPng(node, {
        cacheBust: true,
        pixelRatio: 3,
        width,
        height,
        style: {
            boxShadow: "none",
            transform: "none",
            transformOrigin: "top right",
        },
    });
}

function crc32(bytes) {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        crc ^= bytes[i];
        for (let j = 0; j < 8; j += 1) {
            crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
        }
    }
    return (crc ^ 0xffffffff) >>> 0;
}

function u16(value) {
    const buffer = new ArrayBuffer(2);
    new DataView(buffer).setUint16(0, value, true);
    return new Uint8Array(buffer);
}

function u32(value) {
    const buffer = new ArrayBuffer(4);
    new DataView(buffer).setUint32(0, value, true);
    return new Uint8Array(buffer);
}

function concatBytes(parts) {
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
        out.set(part, offset);
        offset += part.length;
    }
    return out;
}

export function zipStore(files) {
    const localParts = [];
    const centralParts = [];
    let offset = 0;
    const utf8Flag = 0x0800;

    for (const file of files) {
        const nameBytes = new TextEncoder().encode(file.name);
        const data = file.data;
        const crc = crc32(data);
        const localHeader = concatBytes([
            u32(0x04034b50),
            u16(20),
            u16(utf8Flag),
            u16(0),
            u16(0),
            u16(0),
            u32(crc),
            u32(data.length),
            u32(data.length),
            u16(nameBytes.length),
            u16(0),
            nameBytes,
        ]);
        localParts.push(localHeader, data);

        const centralHeader = concatBytes([
            u32(0x02014b50),
            u16(20),
            u16(20),
            u16(utf8Flag),
            u16(0),
            u16(0),
            u16(0),
            u32(crc),
            u32(data.length),
            u32(data.length),
            u16(nameBytes.length),
            u16(0),
            u16(0),
            u16(0),
            u16(0),
            u32(0),
            u32(offset),
            nameBytes,
        ]);
        centralParts.push(centralHeader);
        offset += localHeader.length + data.length;
    }

    const centralDirectory = concatBytes(centralParts);
    const endRecord = concatBytes([
        u32(0x06054b50),
        u16(0),
        u16(0),
        u16(files.length),
        u16(files.length),
        u32(centralDirectory.length),
        u32(offset),
        u16(0),
    ]);

    return concatBytes([...localParts, centralDirectory, endRecord]);
}

function dataUrlToBytes(dataUrl) {
    const comma = dataUrl.indexOf(",");
    const binary = atob(dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

export function triggerBlobDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

export function downloadZip(files, filename) {
    const bytes = zipStore(files);
    triggerBlobDownload(
        new Blob([bytes], { type: "application/zip" }),
        filename || "download.zip"
    );
    return true;
}

function startPrintLayer(nodes, pageSize) {
    const layer = document.createElement("div");
    layer.className = "print-poster-print-layer";
    nodes.forEach((node) => {
        node.classList.add("print-poster--printing");
        layer.appendChild(node);
    });
    const pageStyle = document.createElement("style");
    pageStyle.setAttribute("data-print-poster-page", "true");
    pageStyle.textContent = `@page { size: ${pageSize}; margin: 0; }`;
    document.head.appendChild(pageStyle);
    document.body.appendChild(layer);
    document.body.classList.add("print-poster-mode");
    return { layer, pageStyle };
}

function restorePrintLayer(layer, pageStyle) {
    document.body.classList.remove("print-poster-mode");
    layer.remove();
    pageStyle.remove();
}

export async function printPosterNode(node) {
    if (!node) return false;
    const pageSize = node.getAttribute("data-page-size") || "148mm 210mm";
    const clone = node.cloneNode(true);
    const { layer, pageStyle } = startPrintLayer([clone], pageSize);
    await preparePosterNode(clone);

    let restored = false;
    const restore = () => {
        if (restored) return;
        restored = true;
        restorePrintLayer(layer, pageStyle);
        window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
    window.setTimeout(restore, 60000);
    return true;
}

export async function printPosterNodes({
    baseNode,
    poster,
    items,
    loadQrSrc,
    onProgress,
    signal,
}) {
    if (!baseNode || !items?.length) return false;
    const pageSize = baseNode.getAttribute("data-page-size") || "148mm 210mm";
    const clones = [];
    const qrUrls = [];
    const revokeQrUrls = () => {
        qrUrls.forEach((url) => {
            if (typeof url === "string" && url.startsWith("blob:")) {
                URL.revokeObjectURL(url);
            }
        });
        qrUrls.length = 0;
    };
    try {
        for (let index = 0; index < items.length; index += 1) {
            if (signal?.aborted) {
                revokeQrUrls();
                return false;
            }
            const item = items[index];
            onProgress?.(index + 1, items.length);
            const qrSrc = await loadQrSrc(item);
            qrUrls.push(qrSrc);
            clones.push(
                await buildPosterClone(baseNode, {
                    poster,
                    itemName: item.name,
                    qrSrc,
                })
            );
        }
        if (signal?.aborted) {
            revokeQrUrls();
            return false;
        }
        const { layer, pageStyle } = startPrintLayer(clones, pageSize);
        await preparePosterNode(layer);
        revokeQrUrls();

        let restored = false;
        const restore = () => {
            if (restored) return;
            restored = true;
            restorePrintLayer(layer, pageStyle);
            window.removeEventListener("afterprint", restore);
        };
        window.addEventListener("afterprint", restore);
        window.print();
        window.setTimeout(restore, 60000);
        return true;
    } catch (error) {
        revokeQrUrls();
        clones.forEach((clone) => clone.remove());
        throw error;
    }
}

export async function downloadPosterPng({ node, filename }) {
    if (!node) return false;
    const dataUrl = await posterNodeToPngDataUrl(node);
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = filename || "print-poster.png";
    link.click();
    return true;
}

export async function downloadAllPosterPngs({
    baseNode,
    poster,
    items,
    loadQrSrc,
    zipName,
    onProgress,
    signal,
}) {
    if (!baseNode || !items?.length) return false;
    const files = [];
    for (let index = 0; index < items.length; index += 1) {
        if (signal?.aborted) return false;
        const item = items[index];
        onProgress?.(index + 1, items.length);
        const qrSrc = await loadQrSrc(item);
        const clone = await buildPosterClone(baseNode, {
            poster,
            itemName: item.name,
            qrSrc,
        });
        const host = mountCaptureHost(clone);
        try {
            const dataUrl = await posterNodeToPngDataUrl(clone);
            files.push({
                name: `${safePosterFilename(item.name)}-${item.id}-poster.png`,
                data: dataUrlToBytes(dataUrl),
            });
        } finally {
            host.remove();
            if (typeof qrSrc === "string" && qrSrc.startsWith("blob:")) {
                URL.revokeObjectURL(qrSrc);
            }
        }
    }
    if (signal?.aborted) return false;
    downloadZip(files, zipName || "print-posters.zip");
    return true;
}

export async function downloadAllQrPngs({
    items,
    loadQrBlob,
    zipName,
    onProgress,
    signal,
}) {
    if (!items?.length) return false;
    const files = [];
    for (let index = 0; index < items.length; index += 1) {
        if (signal?.aborted) return false;
        const item = items[index];
        onProgress?.(index + 1, items.length);
        const blob = await loadQrBlob(item);
        files.push({
            name: `${safePosterFilename(item.name)}-${item.id}.png`,
            data: new Uint8Array(await blob.arrayBuffer()),
        });
    }
    if (signal?.aborted) return false;
    downloadZip(files, zipName || "qr-codes.zip");
    return true;
}
